import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoCircleOutlined from '@ant-design/icons/lib/icons/InfoCircleOutlined';
import PrinterOutlined from '@ant-design/icons/lib/icons/PrinterOutlined';
import UserOutlined from '@ant-design/icons/lib/icons/UserOutlined';

import { opTime } from 'utils/dates';
import { OpList } from 'new-components/DLS/OpList/OpList';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpCard } from 'new-components/DLS/OpCard/OpCard';
import { OpCol } from 'new-components/DLS/OpCol/OpCol';
import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpRow } from 'new-components/DLS/OpRow/OpRow';
import { useSelectorJs } from 'utils/customHooks';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { getOpParams } from 'utils/getOpParams';
import { heliumSend } from 'utils/helpers';

import { DATE_TIME_AM_PM_FORMAT } from '../../../../../constants/dates';
import { toLocalByTimezone } from '../../../../../utils/dates';
import STATUS from '../../../../../constants/status';
import { VisitorInvitationState } from '../../../../../store/slices/visitorInvitationSlice';
import { VisitsState } from '../../../../../store/slices/visitsSlice';
import { ImmutableRootState } from '../../../../../store/store';
import { DenyDetails } from '../../../../../types/denyTypes';
import { Kiosk } from '../../../../../types/kioskTypes';
import {
  BadgeData,
  formatVisitDate,
  formatVisitorName,
  printBadge,
} from '../../../../../utils/badgePrinting';
import { formatFullName } from '../../../../../utils/utils';
import {
  getStatusColor,
  getStatusNameById,
} from '../../../../../utils/visitorsHelper';

interface ProfileTabProps {
  visitorPhotoUrl: string | null;
  denyDetails?: DenyDetails;
  connectedKiosks: Kiosk[];
  hasDenyRead: boolean;
}

interface PrintBadgeValues {
  kioskSelect?: string;
}

const ProfileTab = ({
  visitorPhotoUrl,
  denyDetails,
  connectedKiosks,
  hasDenyRead,
}: ProfileTabProps) => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const selectedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisitor']),
  ) as VisitsState['selectedVisitor'];
  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisitorPhotoModalOpen, setIsVisitorPhotoModalVisible] =
    useState(false);
  const [badgePrintModalOpen, setBadgePrintModalOpen] = useState(false);
  const [isDeniedEntryModalOpen, setIsDeniedEntryModalOpen] = useState(false);

  const [printBadgeForm] = OpForm.useForm();

  const confirmModal = useConfirmModal();

  // Check permissions for resend invitation
  const { hasWritePermission: canResendInvitation } =
    useEndpointPermissions('invitationEmail');

  const tz = selectedVisit?.location?.timezone;

  const initialValues = {
    fullName: formatFullName(
      selectedVisitor?.firstName!,
      selectedVisitor?.middleName!,
      selectedVisitor?.lastName!,
    ),
    status: getStatusNameById(
      selectedVisitor?.status!,
      selectedVisit?.scheduleStart!,
      tz,
    ),
    email: selectedVisitor?.email || '',
    mobilePhone: selectedVisitor?.mobilePhone || '',
    signIn: selectedVisitor?.signIn
      ? toLocalByTimezone(selectedVisitor.signIn, tz)?.format(
          DATE_TIME_AM_PM_FORMAT,
        )
      : undefined,
    signOut: selectedVisitor?.signOut
      ? toLocalByTimezone(selectedVisitor.signOut, tz)?.format(
          DATE_TIME_AM_PM_FORMAT,
        )
      : undefined,
  };

  // Schedule times are stored as UTC; compare them in the visit location's
  // timezone (like the Details tab and signIn/signOut) so the past-visit guard
  // doesn't misfire near day boundaries.
  const scheduleStartLocal = toLocalByTimezone(
    selectedVisit?.scheduleStart,
    tz,
  );
  const scheduleEndLocal = toLocalByTimezone(selectedVisit?.scheduleEnd, tz);
  const nowLocal = tz ? opTime().tz(tz) : opTime();

  const resendDisabled = !!(
    !canResendInvitation ||
    selectedVisitor?.status !== STATUS.PENDING.id ||
    !initialValues.email ||
    !invitationConfig?.sendEmail ||
    (scheduleStartLocal &&
      scheduleEndLocal &&
      !nowLocal.isBetween(scheduleStartLocal, scheduleEndLocal, null, '[]') &&
      scheduleStartLocal.isBefore(nowLocal, 'day'))
  );

  const resendInvitation = () => {
    confirmModal({
      title: t('visitors.profile.confirmResend.title'),
      content: t('visitors.profile.confirmResend.message'),
      okText: t('visitors.profile.confirmResend.ok'),
      cancelText: t('visitors.profile.confirmResend.cancel'),
      onOk: async () => {
        try {
          const result = await heliumSend(
            'invitationEmail',
            [orgId, selectedVisit?.id],
            {
              visitorId: selectedVisitor?.id,
            },
          );

          // heliumRequest never throws; it returns an errorMessage on failure
          // (invalid endpoint, 4xx/5xx, network), so we must inspect the result.
          if (result?.errorMessage) {
            notificationService.show(
              'error',
              t('visitors.profile.notification.resendError'),
            );
            return;
          }

          notificationService.show(
            'success',
            t('visitors.profile.notification.resendSuccess'),
          );
        } catch {
          notificationService.show(
            'error',
            t('visitors.profile.notification.resendError'),
          );
        }
      },
    });
  };

  const handleStatusClick = () => {
    if (selectedVisitor?.status === STATUS.DENIED_ENTRY.id) {
      setIsDeniedEntryModalOpen(true);
    }
  };

  const handlePrintBadge = async ({
    values,
  }: OnSubmitArgs<PrintBadgeValues>) => {
    const selectedKiosk = values.kioskSelect;

    if (!selectedKiosk) {
      notificationService.show(
        'error',
        t('visitors.profile.notification.selectKioskError'),
      );
      return;
    }

    setIsSubmitting(true); // Disable button to prevent multiple clicks

    try {
      await heliumSend(
        'processVisitorBadge',
        [orgId, selectedVisit?.id, selectedVisitor?.id],
        { deviceId: selectedKiosk },
      );
      notificationService.show(
        'success',
        t('visitors.profile.notification.printSuccess'),
      );
      setBadgePrintModalOpen(false);
    } catch {
      notificationService.show(
        'error',
        t('visitors.profile.notification.printError'),
      );
    } finally {
      setIsSubmitting(false); // Re-enable the button
    }
  };

  const handleBrowserPrintBadge = async () => {
    const badgeData: BadgeData = {
      visitorType: selectedVisit?.visitorType?.name || t('sco_data_visitor'),
      visitorName: formatVisitorName(
        selectedVisitor?.firstName || null,
        selectedVisitor?.middleName || null,
        selectedVisitor?.lastName || null,
      ),
      visitorCompany: selectedVisit?.company || '',
      hostLabel: t('visitors.table.host.label'),
      hostName: selectedVisit?.host?.firstName
        ? `${selectedVisit.host.firstName} ${selectedVisit.host.lastName || ''}`.trim()
        : '',
      orgName: selectedVisit?.location?.name || '',
      visitDate: formatVisitDate(selectedVisit?.scheduleStart || null),
    };

    setIsSubmitting(true);

    try {
      await printBadge(badgeData);
    } catch {
      notificationService.show(
        'error',
        t('visitors.profile.notification.printError'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <OpForm
        // Ant Design only applies initialValues on mount, so remount the form
        // when the underlying visitor data changes (e.g. after an edit) to
        // reflect updated values without reopening the drawer.
        key={JSON.stringify(initialValues)}
        hasError={false}
        defaultButtons={false}
        initialValues={initialValues}
        isReadOnly
      >
        <OpRow gutter={24}>
          <OpCol span={12}>
            <OpForm.Input
              readOnly
              label={t('visitors.profile.form.fullName')}
              name="fullName"
            />
            <OpRow gutter={16}>
              <OpCol
                span={
                  selectedVisitor?.status === STATUS.DENIED_ENTRY.id &&
                  hasDenyRead
                    ? 21
                    : 24
                }
              >
                <OpForm.Input
                  readOnly
                  label={t('visitors.profile.form.status')}
                  name="status"
                  style={{
                    backgroundColor: getStatusColor(
                      getStatusNameById(
                        selectedVisitor?.status!,
                        selectedVisit?.scheduleStart!,
                        tz,
                      ),
                    ),
                    color: 'white',
                    WebkitTextFillColor: 'white',
                    opacity: 1,
                  }}
                />
              </OpCol>
              {selectedVisitor?.status === STATUS.DENIED_ENTRY.id &&
                hasDenyRead && (
                  <OpCol span={3}>
                    <OpButton
                      style={{ width: '30px', marginTop: '30px' }}
                      icon={<InfoCircleOutlined />}
                      onClick={handleStatusClick}
                      // Disabled when there are no deny details to show, so the
                      // modal never renders empty.
                      disabled={!denyDetails}
                    />
                  </OpCol>
                )}
            </OpRow>

            <OpForm.PhoneInput
              readOnly
              label={t('visitors.profile.form.mobilePhone')}
              name="mobilePhone"
            />
          </OpCol>
          <OpCol span={12}>
            <OpCard
              hoverable={!!visitorPhotoUrl}
              style={{
                width: '100%',
                height: '280px',
                borderRadius: '30px',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={() => {
                if (visitorPhotoUrl) {
                  setIsVisitorPhotoModalVisible(true);
                }
              }}
            >
              {visitorPhotoUrl ? (
                <img
                  src={visitorPhotoUrl}
                  alt="Visitor"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '30px',
                  }}
                  onError={() => {}}
                />
              ) : (
                <UserOutlined
                  style={{
                    fontSize: '64px',
                    color: 'var(--colorTextQuaternary)',
                  }}
                />
              )}
            </OpCard>
          </OpCol>
        </OpRow>
        <OpRow gutter={16}>
          <OpCol span={18}>
            <OpForm.Input
              readOnly
              label={t('visitors.profile.form.email')}
              name="email"
            />
          </OpCol>
          <OpCol span={6}>
            <OpButton
              style={{
                width: '100%',
                marginTop: '30px',
                fontSize: 'clamp(12px, 2vw, 14px)',
              }}
              onClick={resendInvitation}
              disabled={resendDisabled}
            >
              {t('visitors.profile.form.resendInvitation')}
            </OpButton>
          </OpCol>
        </OpRow>
        <OpRow gutter={16}>
          <OpCol span={12}>
            <OpForm.Input
              readOnly
              label={t('visitors.profile.form.signIn')}
              name="signIn"
            />
          </OpCol>
          <OpCol span={12}>
            <OpForm.Input
              readOnly
              label={t('visitors.profile.form.signOut')}
              name="signOut"
            />
          </OpCol>
        </OpRow>
        {selectedVisitor?.status !== STATUS.DENIED_ENTRY.id && (
          <OpRow gutter={12} style={{ marginTop: '10px' }}>
            <OpCol>
              <OpButton
                onClick={handleBrowserPrintBadge}
                disabled={isSubmitting}
                icon={<PrinterOutlined />}
              >
                {t('visitors.profile.form.printBadge')}
              </OpButton>
            </OpCol>
            {connectedKiosks.length > 0 && (
              <OpCol>
                <OpButton
                  onClick={() => setBadgePrintModalOpen(true)}
                  disabled={isSubmitting}
                  icon={<PrinterOutlined />}
                >
                  {t('visitors.profile.form.printBadgeViaKiosk')}
                </OpButton>
              </OpCol>
            )}
          </OpRow>
        )}
      </OpForm>

      {badgePrintModalOpen && (
        <OpModal
          title={t('visitors.profile.modal.badgePrintTitle')}
          open={badgePrintModalOpen}
          onOk={() => {
            printBadgeForm.submit();
          }}
          onCancel={() => setBadgePrintModalOpen(false)}
          okButtonProps={{ disabled: isSubmitting }}
          cancelButtonProps={{ disabled: isSubmitting }}
          width={720}
        >
          <OpDivider />

          <OpForm
            form={printBadgeForm}
            initialValues={{
              kioskSelect:
                connectedKiosks.length > 0 ? connectedKiosks[0].id : undefined,
            }}
            onSubmit={handlePrintBadge}
            hasError={false}
            defaultButtons={false}
          >
            <OpForm.Select
              name="kioskSelect"
              options={connectedKiosks.map((kiosk) => ({
                label: kiosk.label,
                value: kiosk.id,
              }))}
            />
          </OpForm>
        </OpModal>
      )}

      {isVisitorPhotoModalOpen && (
        <OpModal
          open={isVisitorPhotoModalOpen}
          title={t('visitors.profile.modal.visitorPhotoTitle')}
          centered
          onCancel={() => setIsVisitorPhotoModalVisible(false)} // This handles closing when "X" is clicked
          footer={[
            <OpButton
              key="close"
              type="primary"
              onClick={() => setIsVisitorPhotoModalVisible(false)}
            >
              {t('visitors.profile.modal.close')}
            </OpButton>,
          ]}
        >
          {visitorPhotoUrl && (
            <img
              src={visitorPhotoUrl}
              alt="Visitor"
              style={{ width: '100%' }}
            />
          )}
        </OpModal>
      )}

      {isDeniedEntryModalOpen && (
        <OpModal
          open={isDeniedEntryModalOpen}
          title={t('visitors.profile.modal.deniedEntryTitle')}
          centered
          width={720}
          onCancel={() => setIsDeniedEntryModalOpen(false)}
          footer={[
            <OpButton
              key="close"
              type="primary"
              onClick={() => setIsDeniedEntryModalOpen(false)}
            >
              {t('visitors.profile.modal.close')}
            </OpButton>,
          ]}
        >
          <OpList
            bordered
            dataSource={[
              {
                title: t('visitors.profile.modal.reasonForDenial'),
                value: denyDetails?.reason?.name || '---',
              },
              {
                title: t('visitors.profile.modal.effectiveDate'),
                value: denyDetails?.deniedAt
                  ? opTime(denyDetails?.deniedAt)
                      .utc(true)
                      .local()
                      .format(DATE_TIME_AM_PM_FORMAT)
                  : '---',
              },
            ]}
            renderItem={(item: unknown) => {
              const listItem = item as { title: string; value: string };

              return (
                <OpList.Item>
                  <strong>{listItem.title}</strong>{' '}
                  <span>{listItem.value}</span>
                </OpList.Item>
              );
            }}
          />
        </OpModal>
      )}
    </>
  );
};

export default ProfileTab;
